import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Circle, ArrowRight } from '@phosphor-icons/react';
import { MERCHANT_ROUTES, API_ENDPOINTS, apiClient, useAuthStore } from '@dukene/shared';

interface StoreData {
  logo?: string;
}

interface Category {
  id: string;
}

interface ProductsResponse {
  pagination?: {
    total: number;
  };
}

interface ShippingZone {
  id: string;
}

interface OnboardingChecklistProps {
  onVisibilityChange?: (isVisible: boolean) => void;
}

export default function OnboardingChecklist({ onVisibilityChange }: OnboardingChecklistProps) {
  const activeStoreId = useAuthStore((state) => state.activeStoreId);

  const { data: store, isLoading: storeLoading } = useQuery<StoreData>({
    queryKey: ['merchant-store', activeStoreId],
    queryFn: () => apiClient.get<StoreData>(API_ENDPOINTS.MERCHANT.STORE),
    enabled: !!activeStoreId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const hasBranding = !!store?.logo;

  const { data: categories = [], isLoading: categoriesLoading } = useQuery<Category[]>({
    queryKey: ['merchant-categories', activeStoreId],
    queryFn: () => apiClient.get<Category[]>(API_ENDPOINTS.PRODUCTS.CATEGORIES),
    enabled: !!activeStoreId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const hasCategories = Array.isArray(categories) && categories.length >= 3;

  const { data: productsData, isLoading: productsLoading } = useQuery<ProductsResponse>({
    queryKey: ['products-count', activeStoreId],
    queryFn: () => apiClient.get<ProductsResponse>('/products?page=1&limit=1'),
    enabled: !!activeStoreId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const hasProducts = (productsData?.pagination?.total || 0) >= 1;

  const { data: connectedProviders = [], isLoading: paymentsLoading } = useQuery<any[]>({
    queryKey: ['merchant-channels', activeStoreId],
    queryFn: async () => {
      const res = await apiClient.get<any>(API_ENDPOINTS.MERCHANT.CHANNELS.LIST);
      return Array.isArray(res) ? res : res?.channels || res?.data || [];
    },
    enabled: !!activeStoreId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const hasPayments = Array.isArray(connectedProviders) && connectedProviders.some(
    (c: any) => c && (c.status === 'ACTIVE' || c.status === 'VERIFIED' || c.isActive === true || (c.status !== 'REJECTED' && c.status !== 'INACTIVE'))
  );

  const { data: shippingZones = [], isLoading: shippingLoading } = useQuery<ShippingZone[]>({
    queryKey: ['shipping-zones', activeStoreId],
    queryFn: async () => {
      const res = await apiClient.get<any>(API_ENDPOINTS.SHIPPING.ZONES.LIST(activeStoreId!));
      return Array.isArray(res) ? res : res?.data || [];
    },
    enabled: !!activeStoreId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const hasShipping = Array.isArray(shippingZones) && shippingZones.length >= 1;

  const isAllLoading =
    storeLoading || categoriesLoading || productsLoading || paymentsLoading || shippingLoading;

  const isAllComplete =
    hasBranding && hasCategories && hasProducts && hasPayments && hasShipping;

  useEffect(() => {
    if (!isAllLoading && onVisibilityChange) {
      onVisibilityChange(!isAllComplete);
    }
  }, [isAllLoading, isAllComplete, onVisibilityChange]);

  if (isAllLoading || isAllComplete) {
    return null;
  }

  const tasks = [
    {
      id: 'branding',
      title: 'Add store logo & branding',
      description: 'Upload your brand identity so customers recognize your storefront.',
      isComplete: hasBranding,
      href: MERCHANT_ROUTES.SETTINGS_STORE,
    },
    {
      id: 'categories',
      title: 'Create at least 3 categories',
      description: 'Organize your catalog to make products easy to discover.',
      isComplete: hasCategories,
      href: MERCHANT_ROUTES.CATEGORIES,
    },
    {
      id: 'products',
      title: 'Add your first product',
      description: 'List items with pricing, inventory levels, and product photos.',
      isComplete: hasProducts,
      href: MERCHANT_ROUTES.PRODUCTS_NEW,
    },
    {
      id: 'payments',
      title: 'Enable payment methods',
      description: 'Connect M-Pesa or card gateways to accept customer payments.',
      isComplete: hasPayments,
      href: MERCHANT_ROUTES.SETTINGS_PAYMENTS,
    },
    {
      id: 'shipping',
      title: 'Configure shipping zones',
      description: 'Define delivery destinations and courier rates.',
      isComplete: hasShipping,
      href: MERCHANT_ROUTES.SETTINGS_SHIPPING,
    },
  ];

  const completedCount = tasks.filter((t) => t.isComplete).length;
  const progressPercent = Math.round((completedCount / tasks.length) * 100);

  return (
    <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/50 dark:bg-slate-900/40">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-[#202223] dark:text-white">
              Store Launch Checklist
            </h2>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
              Complete these setup tasks to prepare your store for live customer orders
            </p>
          </div>
          <span className="text-xs font-bold text-[#006838] font-mono">
            {progressPercent}% Done
          </span>
        </div>

        <div className="mt-3 h-1.5 w-full bg-[#E1E4E8] dark:bg-slate-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#006838] transition-all duration-500 rounded-full"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      <div className="divide-y divide-[#E1E4E8] dark:divide-slate-800">
        {tasks.map((task) => (
          <Link
            key={task.id}
            to={task.href}
            className="flex items-center justify-between gap-3 p-3.5 hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/40 transition-colors group"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="shrink-0">
                {task.isComplete ? (
                  <CheckCircle weight="fill" className="w-5 h-5 text-[#006838]" />
                ) : (
                  <Circle weight="bold" className="w-5 h-5 text-[#8C9196] group-hover:text-[#006838]" />
                )}
              </div>
              <div className="min-w-0">
                <h3
                  className={`text-xs font-semibold ${
                    task.isComplete
                      ? 'text-[#8C9196] line-through'
                      : 'text-[#202223] dark:text-white'
                  }`}
                >
                  {task.title}
                </h3>
                <p className="text-[11px] text-[#5C5F62] dark:text-slate-400 truncate mt-0.5">
                  {task.description}
                </p>
              </div>
            </div>
            {!task.isComplete && (
              <ArrowRight weight="bold" className="w-4 h-4 text-[#8C9196] group-hover:text-[#006838] shrink-0" />
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}
